<template>
  <div
    class="grid grid-cols-1 gap-6 text-left"
    :aria-busy="disabled ? 'true' : undefined"
    data-testid="invite-fill-mode-step"
  >
    <div>
      <p class="text-gray-500 mb-4">Como você quer preencher a vaga?</p>
      <!-- O SelectableCard emite `click` de um div interno: o wrapper recebe o clique nativo
           que sobe, e carrega o data-testid e o aria-disabled (o card não repassa atributos ao
           elemento clicável). -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-6"
        :class="{ 'pointer-events-none opacity-60': disabled }"
      >
        <div
          data-testid="fill-mode-invite-card"
          :aria-disabled="mainDoctorIsOrphan ? 'true' : undefined"
          :class="mainDoctorIsOrphan ? 'opacity-60 cursor-not-allowed' : ''"
          @click="selectFillMode('INVITE')"
        >
          <SelectableCard
            title="Enviar convite"
            description="O médico recebe o convite. Ele ocupa a vaga quando aceitar o Termo de Ciência."
            :selected="fillMode === 'INVITE'"
            class="!flex"
            :class="{ 'pointer-events-none': mainDoctorIsOrphan }"
          />
        </div>
        <div data-testid="fill-mode-direct-card" @click="selectFillMode('DIRECT')">
          <SelectableCard
            title="Alocar sem convite"
            description="O médico ocupa a vaga agora. Ele só recebe um aviso."
            :selected="fillMode === 'DIRECT'"
            class="!flex"
          />
        </div>
      </div>
      <p
        v-if="mainDoctorIsOrphan"
        class="text-sm text-[#68688D] mt-3"
        data-testid="main-doctor-orphan-notice"
      >
        {{ ORPHAN_TEXT }}
      </p>
    </div>

    <template v-if="fillMode === 'INVITE'">
      <div v-if="hasCurrentDoctor">
        <p class="text-gray-500 mb-4">O que acontece com o médico atual?</p>
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-6"
          :class="{ 'pointer-events-none opacity-60': disabled }"
        >
          <div data-testid="replacement-keep-card" @click="selectReplacement('KEEP')">
            <SelectableCard
              title="Permanece até o aceite"
              description="O médico atual continua no plantão. Ele sai quando um convidado aceitar."
              :selected="replacement === 'KEEP'"
              class="!flex"
            />
          </div>
          <div data-testid="replacement-now-card" @click="selectReplacement('NOW')">
            <SelectableCard
              title="Sai agora"
              description="A vaga fica aberta já. O médico atual recebe um aviso."
              :selected="replacement === 'NOW'"
              class="!flex"
            />
          </div>
        </div>
      </div>

      <div>
        <v-autocomplete
          :model-value="invitees"
          :items="inviteeOptions"
          item-title="title"
          item-value="value"
          item-props="props"
          label="Convidar outros médicos (opcional)"
          variant="outlined"
          color="primary20"
          density="comfortable"
          multiple
          chips
          closable-chips
          clearable
          hide-details
          no-data-text="Nenhum médico encontrado"
          :disabled="disabled"
          data-testid="invitees-select"
          @update:model-value="emit('update:invitees', $event)"
        />
        <p class="text-sm text-[#68688D] mt-2" data-testid="invitees-hint">
          Até {{ maxInvitees }} médicos por convite, contando o escolhido. Quem aceitar primeiro
          fica com todos os plantões.
        </p>
      </div>
    </template>

    <p
      v-else
      class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2"
      data-testid="direct-allocation-warning"
    >
      <span class="font-semibold">A alocação sem convite será desativada em breve.</span>
      Prefira enviar o convite. Assim o médico confirma que sabe do plantão.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import type {
  FillMode,
  ReplacementChoice
} from '@/modules/company/Dashboard/modals/managementDoctor/inviteWizard'

/** Mesmo item do autocomplete de médico do passo 1 (`userId` null = sem cadastro no app). */
export interface InviteDoctorOption {
  title: string
  value: string
  subtitle: string
  userId: number | null
}

const props = defineProps<{
  fillMode: FillMode
  invitees: string[]
  replacement: ReplacementChoice
  doctors: InviteDoctorOption[]
  /** Médico escolhido no passo 1 (`Doctor.id` como string). */
  mainDoctorId: string | null
  /** Plantão já ocupado: pergunta sobre o médico atual (plano WEB-1, P1). */
  hasCurrentDoctor: boolean
  maxInvitees: number
  /** Passo 3 carregando (lista/prévia): trava as escolhas para não divergirem do que vai à API. */
  disabled?: boolean
}>()

const emit = defineEmits<{
  'update:fillMode': [value: FillMode]
  'update:invitees': [value: string[]]
  'update:replacement': [value: ReplacementChoice]
}>()

const ORPHAN_TEXT = 'Este médico ainda não tem cadastro no app. Para ele, use a alocação direta.'

const mainDoctorIsOrphan = computed(() => {
  const main = props.doctors.find((d) => d.value === props.mainDoctorId)
  return !!main && main.userId === null
})

// D58: órfão não recebe convite — a alocação direta vira o único caminho.
watch(
  mainDoctorIsOrphan,
  (orphan) => {
    if (orphan && props.fillMode !== 'DIRECT') emit('update:fillMode', 'DIRECT')
  },
  { immediate: true }
)

// Quem virou o médico principal sai da lista de convidados (senão iria duplicado no doctorIds).
watch(
  () => props.mainDoctorId,
  (id) => {
    if (id && props.invitees.includes(id)) {
      emit(
        'update:invitees',
        props.invitees.filter((v) => v !== id)
      )
    }
  },
  { immediate: true }
)

function selectFillMode(mode: FillMode) {
  if (props.disabled) return
  if (mode === 'INVITE' && mainDoctorIsOrphan.value) return
  emit('update:fillMode', mode)
}

function selectReplacement(choice: ReplacementChoice) {
  if (props.disabled) return
  emit('update:replacement', choice)
}

// Lista de convidados: sem o médico principal; órfão aparece desabilitado com o motivo (D58).
// O backend limita `doctorIds` (principal + convidados) a `maxInvitees`: no teto, só as
// opções já escolhidas seguem habilitadas (para poder desmarcar).
const inviteeOptions = computed(() => {
  const atLimit = props.invitees.length >= props.maxInvitees - 1
  return props.doctors
    .filter((d) => d.value !== props.mainDoctorId)
    .map((d) => ({
      title: d.title,
      value: d.value,
      props: {
        disabled: d.userId === null || (atLimit && !props.invitees.includes(d.value)),
        subtitle: d.userId === null ? ORPHAN_TEXT : d.subtitle
      }
    }))
})
</script>
